import { MetaInfoState } from './metaInfoTypes';
import { useMemo } from 'react';
import { Field, RequiredTextInput } from '@ardoq/forms';
import { Select } from '@ardoq/select';
import type { MetaInfoCommands } from './metaInfoCommands';
import type { SelectedViewCommands } from '../selectedView/commands';
import { SidebarMenu } from '@ardoq/sidebar-menu';
import { SidePanelSection } from '../components/atoms';
import { IconName } from '@ardoq/icons';
import { Box } from '@ardoq/layout';
import {
  buildGenerateDescriptionLabels,
  buildRichTextEditorLabels,
  defaultExtensions,
  RichTextEditor,
  useGenerateDescriptionState,
} from '@ardoq/rich-text-editor';
import { ApiResponse } from '@ardoq/api';
import { getArdoqErrorMessage, isArdoqError } from '@ardoq/common-helpers';
import { AssetType, DirectedTriple } from '@ardoq/api-types';
import { Features, hasFeature } from '@ardoq/features';
import { t, type I18nLocale } from '@ardoq/i18n';
import { translateViewSelectorWarning } from './getViewSelectorWarningMessage';
import { getViewIdUserFriendlyName } from 'views/metaInfoTabs';
import {
  AssetLocationSelect,
  LocationSelect,
} from 'components/LocationSelect/LocationSelect';

export type MetaInfoPanelProps = {
  state: MetaInfoState;
  commands: MetaInfoCommands & SelectedViewCommands;
  generateViewpointDescription: GenerateViewpointDescription;
  paths: DirectedTriple[][];
  i18nLocale: I18nLocale;
};

export type GenerateViewpointDescription = (params: {
  name: string | null;
  description: string | null;
  viewName: string;
  paths: DirectedTriple[][];
}) => ApiResponse<{
  text: string;
  type: 'description' | 'excuse';
}>;

export const MetaInfoPanel = ({
  state: {
    name,
    viewOptions,
    description,
    viewName,
    viewSelectWarning,
    folderId,
    initialState,
  },
  commands,
  generateViewpointDescription,
  paths,
  i18nLocale,
}: MetaInfoPanelProps) => {
  // TODO logic should be moved to a routine
  const generateDescriptionLabels = useMemo(
    () => buildGenerateDescriptionLabels(i18nLocale),
    [i18nLocale]
  );
  const richTextEditorLabels = useMemo(
    () => buildRichTextEditorLabels(i18nLocale),
    [i18nLocale]
  );
  const translatedViewOptions = useMemo(
    () =>
      viewOptions.map(({ value }) => ({
        value,
        label: getViewIdUserFriendlyName(i18nLocale, value),
      })),
    [viewOptions, i18nLocale]
  );
  const {
    state: generateDescriptionState,
    commands: generateDescriptionCommands,
  } = useGenerateDescriptionState(
    generateDescriptionLabels.defaultErrorMessage
  );
  const generateDescription = async () => {
    generateDescriptionCommands.onLoading();
    const response = await generateViewpointDescription({
      name,
      description,
      viewName,
      paths,
    });
    generateDescriptionCommands.onDescriptionReceived();
    if (isArdoqError(response)) {
      generateDescriptionCommands.onError(getArdoqErrorMessage(response));
    } else if (response.type === 'description') {
      commands.setDescription(response.text);
    } else {
      generateDescriptionCommands.onLLMCouldNotGenerateDescription(
        response.text
      );
    }
  };

  return (
    <SidebarMenu
      isPageBodyMenu
      isResizable
      isRightContent
      headerTitle={t(
        i18nLocale,
        'viewpointBuilder:metaInfo.header',
        'Manage the meta info of your viewpoint'
      )}
      headerLeftIconName={IconName.SAVE}
    >
      <Box padding="medium">
        <SidePanelSection flexDirection="column" padding="medium" gap="medium">
          <RequiredTextInput
            label={t(
              i18nLocale,
              'viewpointBuilder:metaInfo.nameLabel',
              'Viewpoint name'
            )}
            dataTestId="viewpoint-name-input"
            value={name ?? ''}
            errorMessage={t(
              i18nLocale,
              'viewpointBuilder:metaInfo.nameRequired',
              'Viewpoint name is required.'
            )}
            onValueChange={commands.setName}
          />
          {initialState?._id ? (
            <AssetLocationSelect
              assetId={initialState._id}
              assetType={AssetType.TRAVERSAL}
            />
          ) : (
            <LocationSelect
              folderId={folderId}
              onChange={commands.setFolderId}
            />
          )}
          <Select
            indicator="required"
            label={t(
              i18nLocale,
              'viewpointBuilder:metaInfo.viewStyleLabel',
              'View style'
            )}
            value={viewName}
            dataTestId="view-style-select"
            warningMessage={translateViewSelectorWarning(
              viewSelectWarning,
              i18nLocale
            )}
            options={translatedViewOptions}
            onChange={commands.setSelectedView}
            i18nLocale={i18nLocale}
          />
          <Field
            label={t(
              i18nLocale,
              'viewpointBuilder:metaInfo.descriptionLabel',
              'Description'
            )}
          >
            <RichTextEditor
              dataTestId="viewpoint-description-textarea"
              content={description ?? ''}
              onInputChange={commands.setDescription}
              toolbarExtensions={defaultExtensions}
              errorMessage={generateDescriptionState.errorMessage}
              warningMessage={
                generateDescriptionState.reasonForNotBeingAbleToGenerateDescription
              }
              generateDescriptionProps={
                hasFeature(Features.AUTOCOMPLETE_OPENAI)
                  ? {
                      onClick: generateDescription,
                      isLoading: generateDescriptionState.isLoading,
                      hasError: generateDescriptionState.hasError,
                      isMissingItemToDescribe: !name,
                      missingItemTooltip:
                        generateDescriptionLabels.missingItemTooltip,
                      infoTooltip:
                        generateDescriptionLabels.generateDescriptionTooltip,
                      label: generateDescriptionLabels.generateLabel,
                    }
                  : undefined
              }
              labels={richTextEditorLabels}
            />
          </Field>
        </SidePanelSection>
      </Box>
    </SidebarMenu>
  );
};
